import { Box, Grid, styled, Tooltip } from '@mui/material';
import FlexBox from 'Components/FlexBox';
import { ThemeButton, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import {
  EGeoDataIssueSeverityLevel,
  IGeoDataIssue,
} from 'FeatureModules/DataImport/types/dataset.types';
import { IPlot } from 'FeatureModules/Site/types/plot.types';
import useDialog from 'hooks/useDialog';
import React, { FC, MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import copyTextToClipboard from 'utils/clipboard.utils';
import { formatAndRoundNumber } from 'utils/number.utils';

export interface IFeatureProperties {
  plot: IPlot;

  // Center of this single cultivation area
  center: [number, number];
  /**
   * Warnings for the plots of the cultivation area
   */
  issues?: IGeoDataIssue[];
  isNeighbouringPlot?: boolean;
}

export interface IPlotInfoPopupProps {
  /* Hide the edit action button in the info popup and replace with view buttons */
  viewOnly?: boolean;
  infoItem: IFeatureProperties;
  onEditPlotClick?: (plot: IPlot) => void;
  onEditFarmClick?: (farmId: string) => void;
}

const Container = styled(Box)(({ theme }) => ({
  background: theme.custom.themeColors.white,
  padding: theme.spacing(1),
  overflow: 'hidden',
}));

const NeighboringContainer = styled(Box)(({ theme }) => ({
  borderRadius: 4,
  padding: theme.spacing(0, 0.5),
  border: `1px solid ${theme.custom.themeColors.grayScale[40]}`,
  background: theme.custom.themeColors.grayScale[30],
}));

const NumberCircle = styled(FlexBox)<{ severity?: 'error' | 'warning' }>(({ theme, severity }) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  justifyContent: 'center',
  background:
    severity === 'error' ? theme.custom.themeColors.error[60] : theme.custom.themeColors.accent[60],
}));

const IssueTag = styled(FlexBox)<{ severity?: 'error' | 'warning' }>(({ theme, severity }) => ({
  borderRadius: theme.spacing(2),
  justifyContent: 'center',
  padding: theme.spacing(0.5, 2),
  background:
    severity === 'error' ? theme.custom.themeColors.error[60] : theme.custom.themeColors.accent[60],
}));

const IssueTextContainer = styled(FlexBox)<{ severity?: 'error' | 'warning' }>(
  ({ theme, severity }) => ({
    borderRadius: 4,
    padding: theme.spacing(0.5),
    gap: theme.spacing(1),
    background:
      severity === 'error'
        ? theme.custom.themeColors.error[20]
        : theme.custom.themeColors.accent[20],
  })
);

const PlotInfoPopup: FC<IPlotInfoPopupProps> = ({
  infoItem: { plot, issues, center, isNeighbouringPlot },
  viewOnly,
  onEditPlotClick,
  onEditFarmClick,
}) => {
  const { closeDialog } = useDialog();
  const navigate = useNavigate();
  const [copiedCoordinates, setCoordinatesCopied] = useState(false);

  const handleCopyCoordinatesToClipboard = (event: MouseEvent) => {
    event.stopPropagation();
    setCoordinatesCopied(true);
    copyTextToClipboard(center.join(', ') || '');
  };

  const warnings = issues?.filter(
    issue => issue.severityLevel === EGeoDataIssueSeverityLevel.WARNING
  );

  const errors = issues?.filter(issue => issue.severityLevel === EGeoDataIssueSeverityLevel.ERROR);

  const handleViewPlotClick = () => {
    navigate(`/sites/${plot.farm.id}/plots/${plot.id}`);
    // Make sure to exit fullscreen mode when navigating to the farm view and close open dialogs
    try {
      document.exitFullscreen();
    } catch {
      // Ignore errors
    }
    closeDialog();
  };

  const handleViewFarmClick = () => {
    navigate(`/sites/${plot.farm.id}`);
    // Make sure to exit fullscreen mode when navigating to the farm view and close open dialogs
    try {
      document.exitFullscreen();
    } catch {
      // Ignore errors
    }
    closeDialog();
  };

  return (
    <Container>
      <FlexBox mb={2}>
        <Icon name="plot-placeholder" size="x-large" mr={2} color="blue-ice" />
        <Box overflow="hidden">
          <FlexBox overflow="hidden">
            <FlexBox overflow="hidden" gap={1}>
              <FlexBox gap={0.5}>
                <ThemeTypography variant="ITEM_TITLE" autoOverflow>
                  {plot.externalId || 'No ID'}
                </ThemeTypography>
                {plot.title && (
                  <>
                    <ThemeTypography variant="ITEM_TITLE" color="GRAY_60">
                      |
                    </ThemeTypography>
                    <ThemeTypography
                      variant="ITEM_TITLE"
                      autoOverflow
                      maxWidth={150}
                      color="GRAY_60"
                    >
                      {plot.title}
                    </ThemeTypography>
                  </>
                )}
              </FlexBox>

              {!!errors?.length && (
                <NumberCircle severity="error">
                  <ThemeTypography variant="BODY_SMALL_BOLD" color="GRAY_100">
                    {errors.length}
                  </ThemeTypography>
                </NumberCircle>
              )}

              {!!warnings?.length && (
                <NumberCircle severity="warning">
                  <ThemeTypography variant="BODY_SMALL_BOLD" color="GRAY_100">
                    {warnings.length}
                  </ThemeTypography>
                </NumberCircle>
              )}

              {isNeighbouringPlot && (
                <NeighboringContainer>
                  <ThemeTypography variant="BODY_SMALL" color="GRAY_80">
                    Neighbouring plot
                  </ThemeTypography>
                </NeighboringContainer>
              )}
            </FlexBox>
          </FlexBox>

          <FlexBox width="100%" gap={1}>
            <FlexBox gap={0.5} maxWidth="calc(100% / 3)">
              <Icon name="tag-filled" size="small" color="gray-60" />
              <ThemeTypography variant="LABEL_INPUT" autoOverflow>
                Farm plot
              </ThemeTypography>
            </FlexBox>

            <FlexBox gap={0.5} maxWidth="calc(100% / 3)">
              <Icon name="location" size="small" color="gray-60" />
              <ThemeTypography variant="LABEL_INPUT" autoOverflow>
                {plot.farm.title} ({plot.farm.externalId})
              </ThemeTypography>
            </FlexBox>

            <FlexBox gap={0.5} maxWidth="calc(100% / 3)">
              <Icon name="raw-material" size="small" color="gray-60" />
              <ThemeTypography variant="LABEL_INPUT" autoOverflow>
                {plot.farm.rawMaterial.title}
              </ThemeTypography>
            </FlexBox>
          </FlexBox>
        </Box>
      </FlexBox>

      {!!errors?.length && (
        <Box display="flex" flexDirection="column" mb={2} gap={1}>
          {errors.map(({ message }, index) => (
            <IssueTextContainer key={`warning-${index}`} severity="error">
              <IssueTag severity="error">
                <ThemeTypography variant="BODY_SMALL_BOLD" color="GRAY_100">
                  Error
                </ThemeTypography>
              </IssueTag>
              <ThemeTypography variant="BODY_SMALL" color="GRAY_100">
                {message}
              </ThemeTypography>
            </IssueTextContainer>
          ))}
        </Box>
      )}

      {!!warnings?.length && (
        <Box display="flex" flexDirection="column" mb={2} gap={1}>
          {warnings.map(({ message }, index) => (
            <IssueTextContainer key={`warning-${index}`} severity="warning">
              <IssueTag severity="warning">
                <ThemeTypography variant="BODY_SMALL_BOLD" color="GRAY_100">
                  Warning
                </ThemeTypography>
              </IssueTag>
              <ThemeTypography variant="BODY_SMALL" color="GRAY_100">
                {message}
              </ThemeTypography>
            </IssueTextContainer>
          ))}
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item>
          <Grid container spacing={1} direction="column">
            <Grid item>
              <ThemeTypography variant="BODY_LARGE_BOLD" color="GRAY_40" textTransform="uppercase">
                Plot area
              </ThemeTypography>
            </Grid>
            <Grid item>
              <ThemeTypography variant="BODY_SMALL">
                {formatAndRoundNumber(plot.sizeHa || 0.04, 2)} ha
              </ThemeTypography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          <Grid container spacing={1} direction="column">
            <Grid item>
              <ThemeTypography variant="BODY_LARGE_BOLD" color="GRAY_40" textTransform="uppercase">
                Plot center
              </ThemeTypography>
            </Grid>
            <Grid item>
              <Tooltip
                leaveDelay={400}
                open={copiedCoordinates}
                title="Copied center coordinates to clipboard"
                onClose={() => setCoordinatesCopied(false)}
              >
                <FlexBox style={{ cursor: 'copy' }} onClick={handleCopyCoordinatesToClipboard}>
                  <ThemeTypography variant="BODY_SMALL">
                    {`${center[0].toFixed(6)}, ${center[1].toFixed(6)}`}
                  </ThemeTypography>
                </FlexBox>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <FlexBox mt={2} gap={1}>
        {!!onEditPlotClick && !viewOnly ? (
          <ThemeButton
            color="BLUE_ICE"
            startIcon={<Icon name="polygon-mapping" />}
            onClick={() => onEditPlotClick(plot)}
          >
            Edit plot
          </ThemeButton>
        ) : (
          <ThemeButton
            color="BLUE_ICE"
            startIcon={<Icon name="link" />}
            onClick={handleViewPlotClick}
          >
            View plot
          </ThemeButton>
        )}

        {!!onEditFarmClick && !viewOnly ? (
          <ThemeButton
            color="BLUE_ICE"
            startIcon={<Icon name="edit" />}
            onClick={() => onEditFarmClick(plot.farm.id)}
          >
            Edit farm
          </ThemeButton>
        ) : (
          <ThemeButton
            color="BLUE_ICE"
            startIcon={<Icon name="link" />}
            onClick={handleViewFarmClick}
          >
            View farm
          </ThemeButton>
        )}
      </FlexBox>
    </Container>
  );
};

export default PlotInfoPopup;
